import { useEffect, useState } from "react";
import { Check, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { METHOD, TOPUP_STATUS, egp, fmtDate, cardCls } from "@/lib/labels";
import { ReasonDialog } from "./ReasonDialog";

const statusCls = { pending: "bg-yellow-500/20 text-yellow-200", approved: "bg-green-500/20 text-green-200", rejected: "bg-red-500/20 text-red-200" };

export const AdminTopups = () => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [rejecting, setRejecting] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = () => api.get("/admin/topups").then((r) => setItems(r.data)).catch((e) => { toast.error(errMsg(e)); setItems([]); });
  useEffect(() => { load(); }, []);
  const update = (d) => setItems((x) => x.map((y) => (y.id === d.id ? d : y)));

  const approve = async (t) => {
    if (busyId) return;
    setBusyId(t.id);
    try { update((await api.post(`/admin/topups/${t.id}/approve`)).data); toast.success(`اتضاف ${egp(t.amount)} لمحفظة ${t.brand_name}`); }
    catch (e) { toast.error(errMsg(e)); load(); }
    finally { setBusyId(null); }
  };
  const reject = async (reason) => {
    try { update((await api.post(`/admin/topups/${rejecting.id}/reject`, { reason })).data); toast.success("الطلب اترفض"); setRejecting(null); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (items === null) return <p className="text-purple-300/70">بنحمّل...</p>;
  const shown = items.filter((t) => filter === "all" || t.status === filter);
  return (
    <section data-testid="admin-topups">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-3xl text-white">طلبات شحن المحفظة <span className="text-[var(--km-pink)]">({shown.length})</span></h2>
        <div className="flex flex-wrap gap-2">
          {Object.entries({ pending: "محتاجة مراجعة", approved: "اتقبلت", rejected: "اترفضت", all: "الكل" }).map(([k, v]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`admin-topups-filter-${k}`} className={`rounded-full px-4 py-1.5 text-sm ${filter === k ? "bg-[var(--km-pink)] text-white" : "bg-white/10 text-purple-200"}`}>{v}</button>
          ))}
          <button onClick={load} className="rounded-full bg-white/10 p-2 text-white" aria-label="تحديث" data-testid="admin-topups-refresh"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </div>
      {shown.length === 0 ? <p className="rounded-3xl border-2 border-dashed border-purple-500/30 p-10 text-center text-purple-300/70" data-testid="admin-topups-empty">مفيش طلبات هنا</p> : (
        <div className="grid gap-4 md:grid-cols-2">
          {shown.map((t, i) => (
            <article key={t.id} className={`${cardCls} flex gap-4 p-4`} data-testid={`admin-topup-${i}`}>
              <a href={`${API}/admin/topups/${t.id}/receipt`} target="_blank" rel="noopener noreferrer" className="h-32 w-24 shrink-0 overflow-hidden rounded-2xl bg-black" data-testid={`admin-topup-receipt-${i}`}>
                <img src={`${API}/admin/topups/${t.id}/receipt`} alt="إيصال التحويل" loading="lazy" className="h-full w-full object-cover" />
              </a>
              <div className="min-w-0 flex-1 space-y-1 text-sm">
                <p className="font-display text-3xl text-white" data-testid={`admin-topup-amount-${i}`}>{egp(t.amount)}</p>
                <p className="font-head font-bold text-white">{t.brand_name}</p>
                <p className="text-purple-200/80">{METHOD[t.method]} · من <span dir="ltr">{t.sender_phone}</span></p>
                <p className="truncate text-xs text-purple-300/60" dir="ltr" style={{ textAlign: "right" }}>{t.email}</p>
                <p className="text-xs text-purple-300/60">{fmtDate(t.created_at)}</p>
                {t.reject_reason && <p className="text-xs text-red-300">{t.reject_reason}</p>}
                {t.status === "pending" ? (
                  <div className="flex gap-2 pt-2">
                    <button onClick={() => approve(t)} disabled={busyId === t.id} className="inline-flex items-center gap-1 rounded-full bg-green-600 px-4 py-2 font-bold text-white disabled:opacity-50" data-testid={`admin-topup-approve-${i}`}><Check className="h-4 w-4" /> موافقة</button>
                    <button onClick={() => setRejecting(t)} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-4 py-2 font-bold text-red-200" data-testid={`admin-topup-reject-${i}`}><X className="h-4 w-4" /> رفض</button>
                  </div>
                ) : <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${statusCls[t.status]}`} data-testid={`admin-topup-status-${i}`}>{TOPUP_STATUS[t.status]}</span>}
              </div>
            </article>
          ))}
        </div>
      )}
      <ReasonDialog open={!!rejecting} title="رفض طلب الشحن" label="سبب الرفض (اختياري، هيوصل للعميل)" required={false} confirmText="ارفض الطلب"
        onClose={() => setRejecting(null)} onConfirm={reject} testId="reject-topup-dialog" />
    </section>
  );
};
